import InputWrapper, { useFieldLabelsBeside } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { useId } from 'react';
import { Control, Controller, Path } from 'react-hook-form';
import { InvoiceEmailFormBase } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

interface SubjectInputProps<T extends InvoiceEmailFormBase> {
    control: Control<T>;
    label?: string;
}

export const SubjectInput = <T extends InvoiceEmailFormBase>({
    control,
    label = 'invoices.email.subject',
}: SubjectInputProps<T>) => {
    const errorId = useId();
    const labelBeside = useFieldLabelsBeside();
    const subjectField = 'subject' as Path<T>;

    return (
        <Controller
            control={control}
            name={subjectField}
            rules={{ required: { value: true, message: t('validation.required-field') } }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <InputWrapper
                    name="subject"
                    label={label}
                    required={true}
                    hasErrors={!!error}
                    gap={0.25}
                    className={labelBeside ? undefined : 'border-divider border-b py-2'}
                >
                    <div>
                        {/* The subject line reads as part of the email, not as a boxed field: no border,
                            no underline, just the text on the sheet. */}
                        <input
                            ref={ref}
                            {...field}
                            type="text"
                            value={(field.value ?? '') as string}
                            name="subject"
                            required={true}
                            aria-invalid={!!error}
                            aria-describedby={error?.message ? errorId : undefined}
                            className={cn(
                                'focus-visible:ring-ring/50 md:text-body1 h-[22px] w-full border-0 bg-transparent p-0 text-base/[1.25] outline-none focus-visible:ring-3',
                                // Beside its label the subject is a boxed field, as the kit input.
                                labelBeside &&
                                    'border-input focus-visible:border-ring h-9 rounded-md border px-2.5 md:text-sm'
                            )}
                        />
                        {error?.message ? (
                            <p id={errorId} className="text-error pt-1 text-xs">
                                {error.message}
                            </p>
                        ) : null}
                    </div>
                </InputWrapper>
            )}
        />
    );
};
